import { useCallback, useEffect, useState } from 'react'
import { ApiError, api, shortDate, type VendorDocument } from '../lib/api'
import { statusWord } from '../lib/status'
import { exceptionsApi, personaNames, severityTone, type ExceptionWorkflow as Workflow } from '../lib/exceptions'
import type { Save } from '../lib/save'
import { ErrorBox, Loading } from './DataTable'
import { ReasonTip } from './vendor/ReasonTip'
import { DateRuleNote } from './DateRuleNote'
import { checkNotAfterToday, checkTargetClosure, todayIso, useLeftFields } from '../lib/dateRules'
import { StageTrack, Status, Toggletip, type Stage } from './kit'
import '../styles/sweep.css'
import '../styles/exceptions.css'

// Section 4 of the product officer's observations, on one exception raised
// under a due diligence parameter: where it is on the track, who reviews and
// who decides, and the act in front of the reader. The API works out what the
// reader may do and why not (the may* flags and the refusal on the workflow),
// so nothing here decides who is allowed; the matrix chain for an exception
// raised before the workflow is ExceptionChainPanel, beside this.

function trackStages(wf: Workflow): Stage[] {
  return wf.track.map((t) => ({
    key: t.key,
    label: t.label,
    state:
      t.state === 'stopped'
        ? wf.status === 'REJECTED' || wf.status === 'REMEDIATION_REQUIRED'
          ? 'rejected'
          : 'blocked'
        : t.state,
  }))
}

function asError(err: unknown): ApiError {
  return err instanceof ApiError ? err : new ApiError(0, 'UNKNOWN', String(err))
}

function todayISO(): string {
  const d = new Date()
  d.setHours(12, 0, 0, 0)
  return d.toISOString().slice(0, 10)
}

type Result = Workflow | { workflow: Workflow; note?: string }

export function ExceptionWorkflowPanel({
  exceptionId,
  save,
  onChanged,
}: {
  exceptionId: string
  // Every step of the workflow (clarify, send for review, review, decide,
  // close) is sent by one act of the save of the dialog this is drawn in
  // (lib/save.ts), so one of them out locks them all, as it did, and still
  // does when the dialog is closed and opened again.
  save: Save
  onChanged: (note: string) => void
}) {
  const [wf, setWf] = useState<Workflow | null>(null)
  const [error, setError] = useState<unknown>(null)
  const [actError, setActError] = useState<ApiError | null>(null)
  const { busy, send } = save
  const [docs, setDocs] = useState<VendorDocument[] | null>(null)
  // In review, the clarification is still open to the person who raised it
  // until the review is recorded, behind a button so the reviewer's own form
  // is the first thing they see.
  const [clarifying, setClarifying] = useState(false)

  const load = useCallback(() => {
    setError(null)
    exceptionsApi.workflow(exceptionId).then(setWf).catch(setError)
  }, [exceptionId])
  // Read when nothing is out: on opening, and again when a step comes back,
  // so a dialog opened while a step was out does not go on offering it.
  useEffect(() => {
    if (!busy) load()
  }, [busy, load])

  const wantsDocs = wf != null && ((wf.mayClarify && (wf.status === 'PENDING' || clarifying)) || wf.mayClose)
  const vendorId = wf?.vendorId
  useEffect(() => {
    if (!wantsDocs || !vendorId) return
    let live = true
    api
      .vendorDocuments(vendorId)
      .then((r) => {
        if (live) setDocs(r.documents)
      })
      .catch(() => {
        if (live) setDocs([])
      })
    return () => {
      live = false
    }
  }, [wantsDocs, vendorId])

  function act(p: Promise<Result>, done: string) {
    setActError(null)
    send(p, {
      shown: (r) => {
        setWf('workflow' in r ? r.workflow : r)
        setClarifying(false)
      },
      done: (r) => onChanged(('note' in r && r.note) || done),
      failed: (err) => setActError(asError(err)),
    })
  }

  if (error) return <ErrorBox error={error} what="This exception's workflow" />
  if (!wf) return <Loading what="the workflow" />

  const decision = wf.decision
  return (
    <div className="dd-form">
      <StageTrack stages={trackStages(wf)} label={'Where this exception is: ' + wf.statusName} />
      <p className="summary-line sw-row">
        <span>{wf.nextStep}</span>
        {wf.refusal && <ReasonTip label="Why you cannot act on it" text={wf.refusal} />}
      </p>

      <dl className="prose-kv">
        <dt>Parameter</dt>
        <dd>{wf.parameterName || wf.parameterCode}</dd>
        <dt>Severity</dt>
        <dd>
          <Status tone={severityTone(wf.severity)}>{wf.severityName}</Status>
          {wf.escalatedFrom && <span className="dd-sub">Escalated from {wf.escalatedFrom}</span>}
        </dd>
        <dt>Reviewed by</dt>
        <dd>{wf.reviewerName || 'Not routed yet'}</dd>
        <dt>Decided by</dt>
        <dd>
          {decision ? personaNames(decision.deciders) : '-'}
          {decision && <span className="dd-sub">{decision.levelName} level</span>}
          {wf.decidePersona && (
            <span className="dd-sub">In the risk officer's place, for this parameter</span>
          )}
        </dd>
        <dt>Corrective action</dt>
        <dd>{wf.correctiveAction || 'Not recorded yet'}</dd>
        <dt>Closure due</dt>
        <dd>
          <span className="prose-mono">{wf.targetClosureOn ? shortDate(wf.targetClosureOn) : 'Not set'}</span>
          {wf.closureOverdue && (
            <span className="dd-sub">
              <Status tone="crit">Overdue</Status>
            </span>
          )}
        </dd>
        {wf.vendorResponse && (
          <>
            <dt>Vendor's response</dt>
            <dd>
              {wf.vendorResponse}
              <span className="dd-sub">
                {wf.vendorResponseOn ? 'Received ' + shortDate(wf.vendorResponseOn) : ''}
                {wf.vendorResponseBy ? ', recorded by ' + wf.vendorResponseBy : ''}
              </span>
            </dd>
          </>
        )}
        <dt>Evidence</dt>
        <dd>
          {wf.supportingEvidence.length === 0
            ? 'None filed'
            : wf.supportingEvidence.length + ' document' + (wf.supportingEvidence.length === 1 ? '' : 's')}
        </dd>
      </dl>

      {actError && <ErrorBox error={actError} what="That step" verb="recorded" />}

      {wf.mayClarify && (wf.status === 'PENDING' || clarifying) && (
        <ClarifyForm wf={wf} docs={docs} busy={busy} onSave={(body) => act(exceptionsApi.clarify(wf.id, body), 'Clarification recorded.')} />
      )}
      {wf.mayClarify && wf.status !== 'PENDING' && !clarifying && !wf.mayReview && (
        <div className="prose-buttons">
          <button type="button" onClick={() => setClarifying(true)}>
            Update the clarification
          </button>
        </div>
      )}
      {wf.mayRoute && wf.status === 'PENDING' && (
        <div className="prose-buttons">
          <button
            type="button"
            className="primary"
            disabled={busy}
            onClick={() => act(exceptionsApi.route(wf.id), 'Sent for review.')}
            data-action="tprm.exceptions.a03"
          >
            Send for review
          </button>
        </div>
      )}

      {wf.review && (
        <section className="dd-section" aria-label="The review">
          <h3>Review</h3>
          <p className="prose-line">
            <Status tone={wf.review.recommendation === 'APPROVE' ? 'ok' : 'crit'}>
              {wf.review.recommendation === 'APPROVE' ? 'Recommends approval' : 'Recommends rejection'}
            </Status>
            <span>
              {wf.reviewerName}, {wf.review.reviewedBy}, {shortDate(wf.review.reviewedAt)}
            </span>
          </p>
          <p className="prose-quote">{wf.review.note}</p>
        </section>
      )}
      {wf.mayReview && <ReviewForm busy={busy} onReview={(rec, note) => act(exceptionsApi.review(wf.id, rec, note), 'Review recorded.')} />}

      {decision && (wf.status === 'IN_REVIEW' || wf.status === 'ESCALATED') && wf.review && (
        <DecisionForm wf={wf} busy={busy} onDecide={(d, reason, step) => act(exceptionsApi.decide(wf.id, d, reason, step), 'Decision recorded.')} />
      )}
      {decision && ['APPROVED', 'CLOSED', 'EXPIRED'].includes(wf.status) && (
        <section className="dd-section" aria-label="The decision">
          <h3>Approved</h3>
          <ol className="dd-steplist">
            {decision.steps.map((st) => (
              <li key={st.index}>
                <Status tone="ok">{st.personaName}</Status>
                <span className="dd-grow">
                  {st.signedBy}
                  {st.signedAt ? ', ' + shortDate(st.signedAt) : ''}
                </span>
              </li>
            ))}
          </ol>
          {wf.decisionReason && <p className="prose-quote">{wf.decisionReason}</p>}
        </section>
      )}
      {(wf.status === 'REJECTED' || wf.status === 'REMEDIATION_REQUIRED') && wf.decisionReason && (
        <section className="dd-section" aria-label="The decision">
          <h3>{statusWord('exception', wf.status).word}</h3>
          <p className="prose-quote">{wf.decisionReason}</p>
        </section>
      )}

      {wf.mayClose && <CloseForm docs={docs} busy={busy} onClose={(ids, note) => act(exceptionsApi.close(wf.id, ids, note), 'Closed.')} />}
      {wf.status === 'CLOSED' && (
        <section className="dd-section" aria-label="The closure">
          <h3>Closed</h3>
          <p className="prose-line">
            <Status tone="ok">Evidence validated</Status>
            <span>
              {wf.closedBy}
              {wf.closedAt ? ', ' + shortDate(wf.closedAt) : ''}
            </span>
          </p>
          {wf.closureNote && <p className="prose-quote">{wf.closureNote}</p>}
        </section>
      )}
    </div>
  )
}

function DocPicker({
  docs,
  chosen,
  onChange,
  legend,
}: {
  docs: VendorDocument[] | null
  chosen: string[]
  onChange: (ids: string[]) => void
  legend: string
}) {
  return (
    <fieldset className="dd-types">
      <legend>{legend}</legend>
      {docs === null ? (
        <span className="dd-empty">Reading the vendor's documents</span>
      ) : docs.length === 0 ? (
        <span className="dd-empty">No document is filed against this vendor yet.</span>
      ) : (
        <div className="dd-docs">
          {docs.map((d) => (
            <label key={d.id} className="dd-check">
              <input
                type="checkbox"
                checked={chosen.includes(d.id)}
                onChange={(e) => onChange(e.target.checked ? [...chosen, d.id] : chosen.filter((x) => x !== d.id))}
              />
              {d.title}
            </label>
          ))}
        </div>
      )}
    </fieldset>
  )
}

type Clarification = {
  correctiveAction: string
  targetClosureOn: string
  supportingEvidence: string[]
  vendorResponse: string
  vendorResponseOn: string
}

function ClarifyForm({
  wf,
  docs,
  busy,
  onSave,
}: {
  wf: Workflow
  docs: VendorDocument[] | null
  busy: boolean
  onSave: (body: Clarification) => void
}) {
  const [action, setAction] = useState(wf.correctiveAction ?? '')
  const [target, setTarget] = useState(wf.targetClosureOn ?? '')
  const [response, setResponse] = useState(wf.vendorResponse ?? '')
  const [responseOn, setResponseOn] = useState(wf.vendorResponseOn ?? '')
  const [evidence, setEvidence] = useState<string[]>(wf.supportingEvidence)
  // D20 of the date rules, said on the field once the person leaves it. The
  // server holds the same two.
  const left = useLeftFields()
  const targetRule = checkTargetClosure(target, wf.expiresOn ?? '', todayIso())
  const responseRule = checkNotAfterToday('Response received on', responseOn, todayIso())
  const base = 'clarify-' + wf.id
  return (
    <section className="dd-section" aria-labelledby={'clarify-' + wf.id}>
      <h3 id={'clarify-' + wf.id}>The vendor's clarification</h3>
      <label className="dd-field">
        <span>Corrective action</span>
        <textarea rows={2} value={action} onChange={(e) => setAction(e.target.value)} />
      </label>
      <div className="dd-form-row">
        <div>
          <label className="dd-field">
            <span>Target closure date</span>
            <input
              type="date"
              value={target}
              min={todayISO()}
              max={wf.expiresOn}
              onChange={(e) => setTarget(e.target.value)}
              onBlur={() => left.leave('target')}
              aria-invalid={left.shown('target', targetRule) ? true : undefined}
              aria-describedby={left.shown('target', targetRule) ? base + '-target-rule' : undefined}
            />
          </label>
          <DateRuleNote id={base + '-target-rule'} message={left.shown('target', targetRule)} />
        </div>
        <div>
          <label className="dd-field">
            <span>Response received on</span>
            <input
              type="date"
              value={responseOn}
              max={todayISO()}
              onChange={(e) => setResponseOn(e.target.value)}
              onBlur={() => left.leave('responseOn')}
              aria-invalid={left.shown('responseOn', responseRule) ? true : undefined}
              aria-describedby={left.shown('responseOn', responseRule) ? base + '-response-rule' : undefined}
            />
          </label>
          <DateRuleNote id={base + '-response-rule'} message={left.shown('responseOn', responseRule)} />
        </div>
      </div>
      <label className="dd-field">
        <span>Vendor's response</span>
        <textarea rows={2} value={response} onChange={(e) => setResponse(e.target.value)} />
      </label>
      <DocPicker docs={docs} chosen={evidence} onChange={setEvidence} legend="Supporting evidence" />
      <div className="prose-buttons">
        <button
          type="button"
          disabled={busy}
          onClick={() =>
            targetRule || responseRule
              ? left.leave('target', 'responseOn')
              : onSave({
                  correctiveAction: action,
                  targetClosureOn: target,
                  supportingEvidence: evidence,
                  vendorResponse: response,
                  vendorResponseOn: responseOn,
                })
          }
          data-action="tprm.exceptions.a02"
        >
          Save clarification
        </button>
      </div>
    </section>
  )
}

function ReviewForm({ busy, onReview }: { busy: boolean; onReview: (rec: 'APPROVE' | 'REJECT', note: string) => void }) {
  const [rec, setRec] = useState<'APPROVE' | 'REJECT' | ''>('')
  const [note, setNote] = useState('')
  const [issue, setIssue] = useState('')
  function submit() {
    if (!rec) {
      setIssue('Choose approve or reject.')
      return
    }
    if (!note.trim()) {
      setIssue('Say why. The people deciding read this first.')
      return
    }
    setIssue('')
    onReview(rec, note.trim())
  }
  return (
    <section className="dd-section" aria-labelledby="review-h">
      <h3 id="review-h">Your review</h3>
      <div className="dd-chips" role="group" aria-label="Recommendation">
        {(['APPROVE', 'REJECT'] as const).map((r) => (
          <button key={r} type="button" className="chip-toggle" aria-pressed={rec === r} onClick={() => setRec(r)}>
            {r === 'APPROVE' ? 'Recommend approval' : 'Recommend rejection'}
          </button>
        ))}
      </div>
      <label className="dd-field">
        <span>Why</span>
        <textarea rows={3} value={note} onChange={(e) => setNote(e.target.value)} />
      </label>
      {issue && <p className="field-issue">{issue}</p>}
      <div className="prose-buttons">
        <button type="button" className="primary" disabled={busy} onClick={submit} data-action="tprm.exceptions.a04">
          Record review
        </button>
      </div>
    </section>
  )
}

function DecisionForm({
  wf,
  busy,
  onDecide,
}: {
  wf: Workflow
  busy: boolean
  onDecide: (d: 'APPROVE' | 'REJECT' | 'REMEDIATE' | 'ESCALATE', reason: string, step?: number) => void
}) {
  const decision = wf.decision!
  const [reason, setReason] = useState('')
  const [issue, setIssue] = useState('')
  function withReason(d: 'REJECT' | 'REMEDIATE' | 'ESCALATE') {
    if (!reason.trim()) {
      setIssue('Give the reason first. It is what the owner and an auditor read.')
      return
    }
    setIssue('')
    onDecide(d, reason.trim())
  }
  const how =
    decision.steps.length > 1
      ? decision.inOrder
        ? 'Each approval is given in this order, by a different person.'
        : 'Both approvals are needed, in either order, by different people.'
      : 'One approval decides it.'
  return (
    <section className="dd-section" aria-labelledby="decide-h">
      <h3 id="decide-h" className="sw-row">
        Decision, {decision.levelName.toLowerCase()} level
        <Toggletip label="How this level decides">{how}</Toggletip>
      </h3>
      {wf.mayDecide && (
        <label className="dd-field">
          <span>Reason, shown with the decision</span>
          <textarea rows={2} value={reason} onChange={(e) => setReason(e.target.value)} />
        </label>
      )}
      <ol className="dd-steplist">
        {decision.steps.map((st) => (
          <li key={st.index}>
            <Status tone={st.signed ? 'ok' : 'neutral'}>{st.personaName}</Status>
            <span className="dd-grow">
              {st.signed ? 'Approved by ' + st.signedBy + (st.signedAt ? ', ' + shortDate(st.signedAt) : '') : 'Not yet'}
            </span>
            {!st.signed &&
              (st.mayAct ? (
                <button
                  type="button"
                  className="primary"
                  disabled={busy}
                  onClick={() => onDecide('APPROVE', reason.trim(), st.index)}
                  data-action="tprm.exceptions.a05"
                >
                  Approve
                </button>
              ) : (
                <ReasonTip label="Why this is not yours to approve" text={st.refusal} />
              ))}
          </li>
        ))}
      </ol>
      {wf.mayDecide && (
        <>
          {issue && <p className="field-issue">{issue}</p>}
          <div className="prose-buttons">
            <button
              type="button"
              className="danger"
              disabled={busy}
              onClick={() => withReason('REJECT')}
              data-action="tprm.exceptions.a05"
            >
              Reject
            </button>
            <button
              type="button"
              disabled={busy}
              onClick={() => withReason('REMEDIATE')}
              data-action="tprm.exceptions.a05"
            >
              Send back
            </button>
            {wf.mayEscalate && (
              <button
                type="button"
                disabled={busy}
                onClick={() => withReason('ESCALATE')}
                data-action="tprm.exceptions.a05"
              >
                Escalate
              </button>
            )}
          </div>
        </>
      )}
    </section>
  )
}

function CloseForm({ docs, busy, onClose }: { docs: VendorDocument[] | null; busy: boolean; onClose: (ids: string[], note: string) => void }) {
  const [chosen, setChosen] = useState<string[]>([])
  const [note, setNote] = useState('')
  const [issue, setIssue] = useState('')
  function submit() {
    if (chosen.length === 0) {
      setIssue('Choose the document that shows the corrective action was done.')
      return
    }
    if (!note.trim()) {
      setIssue('Say what the evidence shows.')
      return
    }
    setIssue('')
    onClose(chosen, note.trim())
  }
  return (
    <section className="dd-section" aria-labelledby="close-h">
      <h3 id="close-h">Close on evidence</h3>
      <DocPicker docs={docs} chosen={chosen} onChange={setChosen} legend="Closure evidence" />
      <label className="dd-field">
        <span>What the evidence shows</span>
        <textarea rows={2} value={note} onChange={(e) => setNote(e.target.value)} />
      </label>
      {issue && <p className="field-issue">{issue}</p>}
      <div className="prose-buttons">
        <button type="button" className="primary" disabled={busy} onClick={submit} data-action="tprm.exceptions.a06">
          Close exception
        </button>
      </div>
    </section>
  )
}
